import { useOutletContext, Link } from "react-router-dom";
import { Building2, FileText, Users, Mail, MapPin, Phone, Target, Info, ExternalLink, Instagram, Facebook, Youtube, MessageCircle, Clock } from "lucide-react";
import { mediaUrl } from "@/lib/appApi";

export default function Tentang() {
  const { settings = {}, units = [] } = useOutletContext() || {};
  const s = settings || {};

  return (
    <div>
      <div className="bg-dlh-green text-white">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <h1 className="font-serif text-4xl font-extrabold">Tentang DLH</h1>
          <p className="mt-2 text-dlh-greenLight max-w-2xl">Dinas Lingkungan Hidup Kabupaten Mimika</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-10 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          <Card icon={Info} title="Profil Dinas">
            <p className="text-gray-700 leading-relaxed">Dinas Lingkungan Hidup Kabupaten Mimika menyelenggarakan urusan pemerintahan bidang lingkungan hidup, meliputi tata lingkungan dan pertamanan, pengelolaan sampah dan limbah B3, pengendalian pencemaran dan kerusakan lingkungan, serta penataan dan peningkatan kapasitas lingkungan hidup.</p>
            <p className="text-gray-700 leading-relaxed mt-3"><strong>PAPA BERSERI</strong> = Pilah, Angkut, Pengolahan, Proses Akhir. Semangat: <em>Mimika Rumah Kita: Bersih, Sehat, Rindang dan Indah.</em></p>
          </Card>

          {(s.visi || s.misi) ? (
            <Card icon={Target} title="Visi & Misi">
              {s.visi && <><h3 className="font-bold text-dlh-green">Visi</h3><p className="text-gray-700 mb-3">{s.visi}</p></>}
              {s.misi && <><h3 className="font-bold text-dlh-green">Misi</h3><p className="text-gray-700 whitespace-pre-line">{s.misi}</p></>}
            </Card>
          ) : (
            <Card icon={Target} title="Visi & Misi"><p className="text-gray-400 italic">Belum tersedia — akan ditampilkan setelah diisi dan diverifikasi pengelola.</p></Card>
          )}

          <Card icon={Building2} title="Struktur Organisasi (Sekretariat & Bidang)">
            <ul className="space-y-3">
              {units.map((u) => (
                <li key={u.slug} className="pl-3 border-l-2 border-dlh-greenLight">
                  <Link to={`/bidang/${u.slug}`} className="font-semibold text-dlh-green hover:text-dlh-orange leading-snug block">{u.name}</Link>
                  <span className="text-xs text-gray-500">{u.pasal}{u.is_support ? " · Unit pendukung" : ""}</span>
                </li>
              ))}
            </ul>
          </Card>

          {s.officials?.length > 0 && (
            <Card icon={Users} title="Pengelola Portal & Pejabat">
              <ul className="space-y-2">{s.officials.map((o, i) => <li key={i} className="text-gray-700"><strong>{o.name}</strong>{o.position ? ` — ${o.position}` : ""}</li>)}</ul>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          <div className="bg-dlh-greenPale rounded-lg p-5">
            <h3 className="font-serif font-bold text-dlh-green mb-3">Kontak</h3>
            <div className="space-y-2 text-sm text-gray-700">
              {s.address ? <p className="flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5 text-dlh-greenMid shrink-0" />{s.address}</p> : <Empty />}
              {s.email && <p className="flex items-center gap-2"><Mail className="h-4 w-4 text-dlh-greenMid" /><a href={`mailto:${s.email}`} className="hover:text-dlh-orange break-all">{s.email}</a></p>}
              {s.phone && <p className="flex items-center gap-2"><Phone className="h-4 w-4 text-dlh-greenMid" />{s.phone}</p>}
              {s.service_number && <p className="flex items-center gap-2"><Phone className="h-4 w-4 text-dlh-greenMid" />Layanan: {s.service_number}</p>}
              {s.service_hours && <p className="flex items-start gap-2"><Clock className="h-4 w-4 mt-0.5 text-dlh-greenMid shrink-0" />{s.service_hours}</p>}
              {!s.address && !s.email && !s.phone && <p className="text-gray-400 italic">Informasi kontak belum tersedia.</p>}
            </div>
            <SocialLinks social={s.social_media} />
            {s.complaint_channel_url && (
              <a href={s.complaint_channel_url} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-dlh-orange hover:underline">Kanal Pengaduan Resmi <ExternalLink className="h-3.5 w-3.5" /></a>
            )}
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <h3 className="font-serif font-bold text-dlh-green mb-3 flex items-center gap-2"><FileText className="h-4 w-4" /> Dokumen Rujukan</h3>
            <ul className="space-y-3">
              {(s.reference_documents || []).map((d, i) => (
                <li key={i} className="text-sm">
                  {d.url ? <a href={mediaUrl(d.url)} target="_blank" rel="noreferrer" className="font-semibold text-dlh-green hover:text-dlh-orange">{d.title}</a> : <span className="font-semibold text-gray-800">{d.title}</span>}
                  {d.note && <p className="text-xs text-gray-500 mt-0.5">{d.note}</p>}
                </li>
              ))}
            </ul>
          </div>

          {s.portal_managers_note && <div className="bg-white rounded-lg border border-gray-200 p-5 text-sm text-gray-600">{s.portal_managers_note}</div>}
        </div>
      </div>
    </div>
  );
}

function normalizeSocial(kind, val) {
  if (!val) return null;
  const v = String(val).trim();
  if (v.startsWith("http")) return v;
  if (kind === "instagram") return `https://instagram.com/${v.replace(/^@/, "")}`;
  if (kind === "facebook") return `https://facebook.com/${v.replace(/^@/, "")}`;
  if (kind === "youtube") return `https://youtube.com/${v.replace(/^@/, "")}`;
  if (kind === "whatsapp") return `https://wa.me/${v.replace(/[^0-9]/g, "")}`;
  return v;
}

function SocialLinks({ social }) {
  const s = social || {};
  const links = [
    { k: "instagram", icon: Instagram, label: "Instagram" },
    { k: "facebook", icon: Facebook, label: "Facebook" },
    { k: "youtube", icon: Youtube, label: "YouTube" },
    { k: "whatsapp", icon: MessageCircle, label: "WhatsApp" },
  ].map((x) => ({ ...x, url: normalizeSocial(x.k, s[x.k]) })).filter((x) => x.url);
  if (links.length === 0) return null;
  return (
    <div className="mt-4 pt-3 border-t border-dlh-greenLight/30" data-testid="social-links">
      <p className="text-xs font-semibold text-dlh-green mb-2">Media Sosial</p>
      <div className="flex flex-wrap gap-2">
        {links.map(({ k, icon: Icon, label, url }) => (
          <a key={k} href={url} target="_blank" rel="noreferrer" title={label} data-testid={`social-${k}`}
            className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-white text-dlh-greenMid border border-dlh-greenLight/50 hover:bg-dlh-green hover:text-white transition-colors">
            <Icon className="h-4 w-4" />
          </a>
        ))}
      </div>
    </div>
  );
}

function Card({ icon: Icon, title, children }) {  return (
    <section className="bg-white rounded-lg border border-gray-200 p-6">
      <h2 className="font-serif text-xl font-bold text-dlh-green flex items-center gap-2 mb-4"><Icon className="h-5 w-5 text-dlh-orange" /> {title}</h2>
      {children}
    </section>
  );
}
function Empty() { return null; }
